import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../environments/environment';
import { AuthRequest, AuthResponse } from '../models/auth-request.model';
import { Observable } from 'rxjs';
import {
  RefreshTokenRequest,
  RefreshTokenResponse,
} from '../models/refresh-token.model';
import {StringMessage} from "../../shared/errors/models/string-message.model";
import { ESS_BASE_URL, IS_ESS_HOST } from '../utils/app-host';

/**
 * The two applications served by this UI. They share the login page and backend, but the backend
 * only restricts New PTM to the configured login groups; ESS accepts any LDAP user.
 */
export type AuthApp = 'newptm' | 'ess';

const AUTH_RESPONSE_COOKIE = 'authResponse';
const NEW_PTM_TOKEN_COOKIE = 'newptm';
const AUTH_APP_COOKIE = 'authApp';
// Session cookies are always set on the root path so both apps (/ and /ess) can read them
const COOKIE_PATH = '/';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private url = environment.webApi + '/api/auth';
	private currentPassword: string | null = null;

  constructor(private http: HttpClient,
              private cookieService: CookieService,
              private router: Router) {}

  /** On the ESS hostname every page is ESS; elsewhere, ESS pages are under /ess. */
  static appForUrl(url: string): AuthApp {
    if (IS_ESS_HOST) {
      return 'ess';
    }
    const path = url.split(/[?#]/)[0];
    return path === ESS_BASE_URL || path.startsWith(ESS_BASE_URL + '/') ? 'ess' : 'newptm';
  }

  loginUrl(app: AuthApp): string {
    return app === 'ess' ? ESS_BASE_URL + '/login' : '/login';
  }

  homeUrl(app: AuthApp): string {
    return app === 'ess' ? ESS_BASE_URL || '/' : '/';
  }

	setPassword(password: string): void {
		this.currentPassword = btoa(password);
	}

	getPassword(): string | null {
		return this.currentPassword ? atob(this.currentPassword) : null;
	}

	clearPassword(): void {
		this.currentPassword = null;
	}

  /** Both apps sign in here; `ess: true` skips the New PTM login group check. */
  signIn(authRequest: AuthRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(this.url + '/sign-in', authRequest);
  }

  refreshToken(refreshTokenRequest: RefreshTokenRequest): Observable<RefreshTokenResponse> {
    return this.http.post<RefreshTokenResponse>(this.url + '/refresh-token', refreshTokenRequest);
  }

  isRefreshTokenRequest(url: string | null): boolean {
    return !!url && url.endsWith('/refresh-token');
  }

  newPtm(): Observable<StringMessage> {
    return this.http.get<StringMessage>(this.url + '/new-ptm');
  }

  startSession(authResponse: AuthResponse, app: AuthApp): void {
    this.saveAuthResponse(authResponse);
    this.cookieService.set(AUTH_APP_COOKIE, app, { path: COOKIE_PATH });
  }

  saveAuthResponse(authResponse: AuthResponse): void {
    this.cookieService.set(AUTH_RESPONSE_COOKIE, JSON.stringify(authResponse), { path: COOKIE_PATH });
  }

  getAuthResponse(): AuthResponse | null {
    const authResponse = this.cookieService.get(AUTH_RESPONSE_COOKIE);
    return authResponse ? JSON.parse(authResponse) as AuthResponse : null;
  }

  saveNewPtmToken(token: string): void {
    this.cookieService.set(NEW_PTM_TOKEN_COOKIE, JSON.stringify(token), { path: COOKIE_PATH });
  }

  /** Sessions created before the app was tracked are New PTM sessions. */
  getSessionApp(): AuthApp {
    return (this.cookieService.get(AUTH_APP_COOKIE) || 'newptm') as AuthApp;
  }

  /**
   * An ESS session (any LDAP user) is not valid for New PTM, which requires the login group check.
   * A New PTM session is also valid for ESS.
   */
  hasSessionFor(app: AuthApp): boolean {
    if (!this.cookieService.get(AUTH_RESPONSE_COOKIE)) {
      return false;
    }
    return app === 'ess' || this.getSessionApp() === 'newptm';
  }

  /**
   * App of the current page. Until the router completes its first navigation, router.url is still '/', so e.g. a
   * session that expires while an ESS page loads would otherwise log out to the New PTM login.
   */
  private currentApp(): AuthApp {
    return AuthService.appForUrl(this.router.navigated ? this.router.url : window.location.pathname);
  }

  clearSession(): void {
    [AUTH_RESPONSE_COOKIE, NEW_PTM_TOKEN_COOKIE, AUTH_APP_COOKIE]
      .forEach(cookie => this.cookieService.delete(cookie, COOKIE_PATH));
    this.clearPassword();
  }

  /** Ends the session and goes to the login page of the given app (by default, the app of the current page). */
  logout(app: AuthApp = this.currentApp()): void {
    this.clearSession();
    this.router.navigateByUrl(this.loginUrl(app));
  }
}
